/**
 * Utility functions for extracting identity display fields from the ID token's
 * standard OIDC claims (email, name).
 *
 * Authorization and org context are NOT here — under the v2 token contract the
 * SPA never parses a token for those; it reads them from the tenant-management
 * backend. See {@link module:utils/identity}.
 */

import type { Claims } from '../types/claims';

/**
 * Extracts the user's email from JWT claims.
 *
 * @param claims - JWT claims object, or null/undefined if not authenticated
 * @returns The user's email address, or an empty string if not available
 *
 * @example
 * ```ts
 * getUserEmail({ email: 'user@example.com' }) // 'user@example.com'
 * getUserEmail({}) // ''
 * getUserEmail(null) // ''
 * ```
 */
export function getUserEmail(claims: Claims | null | undefined): string {
  if (!claims || typeof claims.email !== 'string') {
    return '';
  }

  return claims.email;
}

/**
 * Extracts a display name from JWT claims.
 *
 * The function prioritizes the `name` claim, falling back to `email` if
 * the name is not available.
 *
 * @param claims - JWT claims object, or null/undefined if not authenticated
 * @returns A display name for the user, or an empty string if neither name nor email is available
 *
 * @example
 * ```ts
 * getUserDisplayName({ name: 'John Doe', email: 'john@example.com' }) // 'John Doe'
 * getUserDisplayName({ email: 'john@example.com' }) // 'john@example.com'
 * getUserDisplayName({}) // ''
 * getUserDisplayName(null) // ''
 * ```
 */
export function getUserDisplayName(claims: Claims | null | undefined): string {
  if (!claims) {
    return '';
  }

  // Prefer name claim
  if (typeof claims.name === 'string' && claims.name.trim()) {
    return claims.name;
  }

  // Fall back to email
  if (typeof claims.email === 'string' && claims.email.trim()) {
    return claims.email;
  }

  return '';
}
